import PropTypes from 'prop-types';
import React from 'react';
import { WeezHeader, Statistics } from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';

import { useTrackingPage } from '../../../components/tracking/trackingProvider';

import StatisticsFetcher from './statisticsFetcher';
import StatisticsAttendees from './statisticsAttendees';
import StatisticsOrders from './statisticsOrders';
import StatisticsRevenues from './statisticsRevenues';
import StatisticsPayments from './statisticsPayments';
import EventSettings from '../../../models/eventSettings';
import Styles from '../../../styles/event/dashboard/eventDashboard.module.css';
import EvolutionSales from './evolutionSales';
import OnboardingChecklist from './onboardingChecklist';
import WarningMessage from '../../../components/message';
import Auth from '../../../utils/auth';

export default function EventDashboard({ organization, event, prefixUrl }) {
    const { t } = useTranslation();

    const [settings, settingsLoading] = EventSettings.useApiModel(
        {
            // Don't try to render a dashboard without its events settings
            strict: true,
            // Required for our model lib to understand this is just one item we want, not a list
            id: null,
            // We also need to tell the model to skip caching objects, as there's no id to link
            // patch and get calls together.
            cache: false,
            allow_cache: false,
            organizationId: organization.id,
            eventId: event.id
        },
        [organization, event]
    );

    const [stats, setStats] = React.useState(null);

    useTrackingPage('VIEW - HOME EVENT', { 'Page Title': 'HOME EVENT' });

    return (
        <>
            <WeezHeader title={t('ticket.eventDashboard.pageTitle')}>{t('ticket.eventDashboard.pageSubtitle')}</WeezHeader>

            {!settingsLoading && <WarningMessage organization={organization} event={event} settings={settings} />}

            <Auth allow={['RoleCapacity.ADMIN', 'PyvarCapacity.ADMIN', 'PyvarCapacity.EVENT_STATS']}>
                <div className={Styles['container']}>
                    <Statistics.Group className={Styles['statistics-container']}>
                        <StatisticsFetcher route={`/ticket/organizations/${organization.id}/events/${event.id}/stats`} onStats={setStats}>
                            <StatisticsAttendees titleKey={'total_attendees'} prefixUrl={prefixUrl} eventStatus={event.get_status()} eventEndDate={event.end_date} />
                            <StatisticsOrders
                                currency={event?.currency}
                                titleKey={'total_orders'}
                                prefixUrl={prefixUrl}
                                eventStatus={event.get_status()}
                                eventEndDate={event.end_date}
                            />
                            <StatisticsRevenues
                                currency={event?.currency}
                                titleKey={'sum_revenue'}
                                prefixUrl={prefixUrl}
                                eventStatus={event.get_status()}
                                eventEndDate={event.end_date}
                            />
                            <StatisticsPayments currency={event?.currency} titleKey={'next_payment'} prefixUrl={prefixUrl} organization={organization} />
                        </StatisticsFetcher>
                    </Statistics.Group>
                    <div className={Styles['evolution-container']}>
                        {!['published', 'closed'].includes(event?.publication_status) && <OnboardingChecklist organization={organization} event={event} />}
                        <EvolutionSales data={stats} />
                    </div>
                </div>
            </Auth>
        </>
    );
}

EventDashboard.propTypes = {
    event: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    organization: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    prefixUrl: PropTypes.string
};
